import { useContext, useEffect, useRef } from 'react'

import { TelemetryContext } from '../context/TelemetryContext'
import { STUDY_EVENTS } from '../constants/telemetryEvents'
import CodeBlock from '../../../study/components/CodeBlock'
import { withInlineCode } from './ProbeModal'
import { idKey } from './probeService'

import './probe-modal.scss'

// What each mark beside an option says: the right answer, and the student's own.
const markOf = (isKey, isChoice) => (isKey && isChoice ? 'Correct answer · your answer' : isKey ? 'Correct answer' : 'Your answer')

/**
 * The concept probes' answers, on the Thank You overlay once the server has every answer
 * (useStudyPhase's review): each question as it was asked, the right option and the student's
 * own marked, and a short explanation. Never before the last answer - the questions themselves
 * say nothing on whether an answer was right (the research lead's choice, 2026-10-28: the
 * answers are shown at the end). Logged once per page as PROBE_REVIEW_SHOWN.
 *   items: the probes as the questions showed them (normalizeProbes'); review: the server's
 *   [{ itemId, choice, correct, key, explanation }].
 */
export default function ProbeReview({ items, review }) {
  const { logTelemetryEvent } = useContext(TelemetryContext)
  const loggedRef = useRef(false)

  useEffect(() => {
    if (loggedRef.current) return
    loggedRef.current = true
    logTelemetryEvent(STUDY_EVENTS.PROBE_REVIEW_SHOWN, {
      items: review.map(({ itemId, choice, correct }) => ({ itemId, choice, correct })),
    })
  }, [logTelemetryEvent, review])

  const byId = new Map(items.map((item) => [idKey(item.id), item]))

  return (
    <div className="probe-review">
      <h3 className="probe-review-title">The answers</h3>
      {review.map((entry, index) => {
        const item = byId.get(idKey(entry.itemId))
        return (
          <div key={idKey(entry.itemId)} className="survey-question probe-question">
            <span className="probe-count">
              Question {index + 1} · {entry.correct ? 'Correct' : 'Incorrect'}
            </span>
            {(item?.blocks || []).map((block, blockIndex) =>
              block.type === 'code' ? (
                <CodeBlock key={blockIndex} code={block.code} lang="python" />
              ) : (
                <p key={blockIndex} className="probe-text">{withInlineCode(block.text)}</p>
              )
            )}
            <div className="probe-options">
              {(item?.options || []).map((option) => {
                const isKey = option.letter === entry.key
                const isChoice = option.letter === entry.choice
                const state = isKey ? ' probe-option-correct' : isChoice ? ' probe-option-wrong' : ''
                return (
                  <div key={option.letter} className={`probe-option probe-option-static${state}`}>
                    <span className="probe-letter">{option.letter}</span>
                    <span className="probe-option-text">{withInlineCode(option.text)}</span>
                    {(isKey || isChoice) && <span className="probe-mark">{markOf(isKey, isChoice)}</span>}
                  </div>
                )
              })}
            </div>
            {entry.explanation && <p className="probe-explanation">{withInlineCode(entry.explanation)}</p>}
          </div>
        )
      })}
    </div>
  )
}
